import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI, cloudStorageAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { US_STATES } from '../lib/utils';
import { toast } from 'sonner';
import { 
  ArrowLeft, Loader2, MapPin, Settings as SettingsIcon, Home, Compass, 
  Palette, Target, ShoppingBag, Layers, Sparkles, X, Plus, CreditCard, Clock, Crown,
  AlertTriangle, Calendar, DollarSign, Receipt, User, Image as ImageIcon, CheckCircle2,
  Link2, Unlink, ExternalLink
} from 'lucide-react';
import { HelpTooltip } from '../components/ui/help-tooltip';

const CRAFT_TYPES = [
  'Jewelry', 'Pottery/Ceramics', 'Woodworking', 'Textiles/Fiber Arts', 
  'Candles/Soap', 'Art/Paintings', 'Photography', 'Leather Goods',
  'Home Decor', 'Food/Baked Goods', 'Plants/Flowers', 'Mixed Media', 'Other'
];

const VENUE_TYPES = ['indoor', 'outdoor', 'mixed'];
const EVENT_SIZES = ['small', 'medium', 'large'];
const US_REGIONS = [
  { value: 'northeast', label: 'Northeast (NY, MA, PA, etc.)' },
  { value: 'southeast', label: 'Southeast (FL, GA, NC, etc.)' },
  { value: 'midwest', label: 'Midwest (IL, OH, MI, etc.)' },
  { value: 'southwest', label: 'Southwest (TX, AZ, NM, etc.)' },
  { value: 'west', label: 'West (CA, CO, WA, etc.)' },
  { value: 'pacific', label: 'Pacific (HI, AK)' }
];

export const Settings = () => {
  const { user, updateSettings } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile & Business settings
  const [homeCity, setHomeCity] = useState(user?.home_city || '');
  const [homeState, setHomeState] = useState(user?.home_state || '');
  const [homeZip, setHomeZip] = useState(user?.home_zip || '');
  const [maxDistance, setMaxDistance] = useState(user?.max_distance || 50);
  const [craftType, setCraftType] = useState(user?.craft_type || '');
  const [businessName, setBusinessName] = useState(user?.business_name || '');
  const [viewCommunityEvents, setViewCommunityEvents] = useState(user?.view_community_events || false);
  const [allowNegativeInventory, setAllowNegativeInventory] = useState(user?.allow_negative_inventory || false);

  // Artisan Profile & Event Matching Preferences
  const [craftDescription, setCraftDescription] = useState(user?.craft_description || '');
  const [showPreferences, setShowPreferences] = useState(user?.show_preferences || '');
  const [preferredVenueTypes, setPreferredVenueTypes] = useState(user?.preferred_venue_types || []);
  const [preferredEventSizes, setPreferredEventSizes] = useState(user?.preferred_event_sizes || []);
  const [preferredRegions, setPreferredRegions] = useState(user?.preferred_regions || []);
  const [matchKeywords, setMatchKeywords] = useState(user?.match_keywords || []);
  const [avoidKeywords, setAvoidKeywords] = useState(user?.avoid_keywords || []);
  const [newMatchKeyword, setNewMatchKeyword] = useState('');
  const [newAvoidKeyword, setNewAvoidKeyword] = useState('');

  // Billing state
  const [subscription, setSubscription] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);

  // Cloud storage connection state
  const [cloudStatus, setCloudStatus] = useState(null);
  const [cloudLoading, setCloudLoading] = useState(true);
  const [connectingGoogle, setConnectingGoogle] = useState(false);
  const [disconnectingGoogle, setDisconnectingGoogle] = useState(false);

  // Payment processing state
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Check URL params for tab navigation, OAuth callbacks, and payment success
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab);
    }
    
    // Handle payment success redirect from Stripe
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    if (paymentStatus === 'success' && sessionId) {
      setActiveTab('billing');
      pollPaymentStatus(sessionId);
    }
    
    // Handle OAuth callback messages
    const googleDriveStatus = searchParams.get('google_drive');
    const error = searchParams.get('error');
    
    if (googleDriveStatus === 'connected') {
      toast.success('Google Drive connected successfully!');
      fetchCloudStatus();
      navigate('/settings?tab=integrations', { replace: true });
    } else if (error === 'oauth_failed') {
      toast.error('Failed to connect Google Drive. Please try again.');
      navigate('/settings?tab=integrations', { replace: true });
    } else if (error === 'missing_scopes') {
      toast.error('Google Drive permissions were not granted. Please try again and allow all requested permissions.');
      navigate('/settings?tab=integrations', { replace: true });
    }
  }, [searchParams, navigate]);

  // Poll Stripe for payment status
  const pollPaymentStatus = async (sessionId, attempt = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2500;
    
    setPaymentProcessing(true);
    
    if (attempt >= maxAttempts) {
      setPaymentProcessing(false);
      toast.error('Payment verification timed out. Please check your email for confirmation or contact support.');
      navigate('/settings?tab=billing', { replace: true });
      return;
    }
    
    try {
      const res = await subscriptionAPI.getCheckoutStatus(sessionId);
      
      if (res.data.payment_status === 'paid') {
        setPaymentProcessing(false);
        setPaymentSuccess(true);
        toast.success('Payment successful! Your subscription is now active.');
        // Refresh subscription status
        fetchBillingData();
        navigate('/settings?tab=billing', { replace: true });
        return;
      } else if (res.data.status === 'expired') {
        setPaymentProcessing(false);
        toast.error('Payment session expired. Please try again.');
        navigate('/settings?tab=billing', { replace: true });
        return;
      }
      
      // Still pending, poll again
      setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
    } catch (error) {
      if (attempt < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), pollInterval);
      } else {
        setPaymentProcessing(false);
        toast.error('Unable to verify payment status. Please refresh the page.');
      }
    }
  };

  // Fetch cloud storage connection status
  const fetchCloudStatus = async () => {
    try {
      setCloudLoading(true);
      const response = await cloudStorageAPI.getStatus();
      setCloudStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch cloud storage status');
    } finally {
      setCloudLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchCloudStatus();
    }
  }, [user]);

  // Fetch billing/subscription status
  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        const [subRes, pricingRes] = await Promise.all([
          subscriptionAPI.getStatus(),
          subscriptionAPI.getPricing()
        ]);
        setSubscription(subRes.data);
        setPricing(pricingRes.data);
      } catch (error) {
        console.error('Failed to fetch billing data');
      } finally {
        setBillingLoading(false);
      }
    };
    
    if (user) {
      fetchBillingData();
    }
  }, [user]);

  if (!user) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await updateSettings({
        home_city: homeCity || null,
        home_state: homeState === 'none' ? null : (homeState || null),
        home_zip: homeZip || null,
        max_distance: parseInt(maxDistance) || 50,
        craft_type: craftType || null,
        business_name: businessName || null,
        view_community_events: viewCommunityEvents,
        allow_negative_inventory: allowNegativeInventory,
        craft_description: craftDescription || null,
        show_preferences: showPreferences || null,
        preferred_venue_types: preferredVenueTypes.length > 0 ? preferredVenueTypes : null,
        preferred_event_sizes: preferredEventSizes.length > 0 ? preferredEventSizes : null,
        preferred_regions: preferredRegions.length > 0 ? preferredRegions : null,
        match_keywords: matchKeywords.length > 0 ? matchKeywords : null,
        avoid_keywords: avoidKeywords.length > 0 ? avoidKeywords : null,
      });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const addMatchKeyword = () => {
    if (newMatchKeyword.trim() && !matchKeywords.includes(newMatchKeyword.trim())) {
      setMatchKeywords([...matchKeywords, newMatchKeyword.trim()]);
      setNewMatchKeyword('');
    }
  };

  const addAvoidKeyword = () => {
    if (newAvoidKeyword.trim() && !avoidKeywords.includes(newAvoidKeyword.trim())) {
      setAvoidKeywords([...avoidKeywords, newAvoidKeyword.trim()]);
      setNewAvoidKeyword('');
    }
  };

  const removeMatchKeyword = (keyword) => setMatchKeywords(matchKeywords.filter(k => k !== keyword));
  const removeAvoidKeyword = (keyword) => setAvoidKeywords(avoidKeywords.filter(k => k !== keyword));
  const toggleVenueType = (type) => setPreferredVenueTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  const toggleEventSize = (size) => setPreferredEventSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
  const toggleRegion = (region) => setPreferredRegions(prev => prev.includes(region) ? prev.filter(r => r !== region) : [...prev, region]);

  // Google Drive connection handlers
  const handleConnectGoogleDrive = async () => {
    try {
      setConnectingGoogle(true);
      const response = await cloudStorageAPI.connectGoogleDrive();
      // Redirect to Google OAuth
      window.location.href = response.data.authorization_url;
    } catch (error) {
      console.error('Failed to initiate Google Drive connection:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect Google Drive');
      setConnectingGoogle(false);
    }
  };

  const handleDisconnectGoogleDrive = async () => {
    if (!window.confirm('Are you sure you want to disconnect Google Drive? You will need to reconnect to import files.')) {
      return;
    }
    
    try {
      setDisconnectingGoogle(true);
      await cloudStorageAPI.disconnectGoogleDrive();
      toast.success('Google Drive disconnected');
      setCloudStatus(prev => ({ ...prev, google_drive: false, google_drive_email: null }));
    } catch (error) {
      console.error('Failed to disconnect Google Drive:', error);
      toast.error('Failed to disconnect Google Drive');
    } finally {
      setDisconnectingGoogle(false);
    }
  };

  const getSubscriptionBadge = () => {
    if (!subscription) return null;
    const badges = {
      trial: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock, label: 'Trial' },
      active: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Active' },
      lifetime: { bg: 'bg-purple-100', text: 'text-purple-700', icon: Crown, label: 'Lifetime' },
      expired: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertTriangle, label: 'Expired' },
      revoked: { bg: 'bg-red-100', text: 'text-red-700', icon: X, label: 'Revoked' }
    };
    const badge = badges[subscription.status] || badges.trial;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-[#4A4A4A] hover:text-[#1E3A5F] mb-6 font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to dashboard
        </Link>

        <div className="card-dashboard" style={{ boxShadow: '6px 6px 0px 0px #1E3A5F' }}>
          <div className="mb-6">
            <div className="flex items-center gap-3 mb-2">
              <SettingsIcon className="w-8 h-8 text-[#1E3A5F]" />
              <h1 className="text-3xl font-bold font-['Outfit'] text-[#1E3A5F]">Settings</h1>
            </div>
            <p className="text-[#4A4A4A]">Manage your account, business preferences, and billing</p>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-8 bg-gray-100 p-1 rounded-xl">
              <TabsTrigger 
                value="profile" 
                className="data-[state=active]:bg-white data-[state=active]:text-[#1E3A5F] data-[state=active]:shadow rounded-lg"
                data-testid="settings-tab-profile"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger 
                value="preferences" 
                className="data-[state=active]:bg-white data-[state=active]:text-[#1E3A5F] data-[state=active]:shadow rounded-lg"
                data-testid="settings-tab-preferences"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Preferences
              </TabsTrigger>
              <TabsTrigger 
                value="integrations" 
                className="data-[state=active]:bg-white data-[state=active]:text-[#1E3A5F] data-[state=active]:shadow rounded-lg"
                data-testid="settings-tab-integrations"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Integrations
              </TabsTrigger>
              <TabsTrigger 
                value="billing" 
                className="data-[state=active]:bg-white data-[state=active]:text-[#1E3A5F] data-[state=active]:shadow rounded-lg"
                data-testid="settings-tab-billing"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Billing
              </TabsTrigger>
            </TabsList>

            {/* PROFILE TAB */}
            <TabsContent value="profile">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Account Info */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <User className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Account Info</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label className="text-sm text-[#71717A]">Name</Label>
                      <p className="font-medium text-[#1E3A5F]">{user.name}</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm text-[#71717A]">Email</Label>
                      <p className="font-medium text-[#1E3A5F]">{user.email}</p>
                    </div>
                  </div>
                </div>

                {/* Business Info */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <Palette className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Business Info</h2>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="businessName" className="font-medium text-[#1E3A5F]">Business Name</Label>
                      <Input
                        id="businessName"
                        placeholder="e.g., Jane's Handmade Jewelry"
                        value={businessName}
                        onChange={(e) => setBusinessName(e.target.value)}
                        className="input-default w-full"
                        data-testid="settings-business-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-medium text-[#1E3A5F]">Craft Type</Label>
                      <Select value={craftType} onValueChange={setCraftType}>
                        <SelectTrigger className="input-default" data-testid="settings-craft-type">
                          <SelectValue placeholder="Select your craft" />
                        </SelectTrigger>
                        <SelectContent className="border-2 border-[#1E3A5F] rounded-xl max-h-60">
                          {CRAFT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Home Location */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <Home className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Home Location</h2>
                  </div>
                  <p className="text-sm text-[#4A4A4A]">Set your home location to calculate distances to events</p>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="homeCity" className="font-medium text-[#1E3A5F]">City</Label>
                      <Input
                        id="homeCity"
                        placeholder="e.g., Austin"
                        value={homeCity}
                        onChange={(e) => setHomeCity(e.target.value)}
                        className="input-default w-full"
                        data-testid="settings-home-city"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label className="font-medium text-[#1E3A5F]">State</Label>
                      <Select value={homeState} onValueChange={setHomeState}>
                        <SelectTrigger className="input-default" data-testid="settings-home-state">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent className="border-2 border-[#1E3A5F] rounded-xl max-h-60">
                          <SelectItem value="none">None</SelectItem>
                          {US_STATES.map((s) => (
                            <SelectItem key={s} value={s}>{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="homeZip" className="font-medium text-[#1E3A5F]">ZIP Code</Label>
                      <Input
                        id="homeZip"
                        placeholder="e.g., 78701"
                        value={homeZip}
                        onChange={(e) => setHomeZip(e.target.value)}
                        className="input-default w-full"
                        data-testid="settings-home-zip"
                      />
                    </div>
                  </div>

                  {(homeCity || homeState) && (
                    <div className="flex items-center gap-2 p-3 bg-[#FFEDDF] rounded-lg">
                      <MapPin className="w-5 h-5 text-[#FE5F55]" />
                      <span className="text-sm text-[#1E3A5F]">
                        Home: {[homeCity, homeState, homeZip].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                <div className="pt-4">
                  <Button type="submit" className="btn-primary" disabled={loading} data-testid="settings-save-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Settings'}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* PREFERENCES TAB */}
            <TabsContent value="preferences">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Event Scout Preferences */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <Compass className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Event Scout Preferences</h2>
                  </div>
                  
                  <div className="space-y-2 max-w-xs">
                    <Label htmlFor="maxDistance" className="font-medium text-[#1E3A5F] flex items-center gap-1">
                      Max Distance (miles)
                      <HelpTooltip content="Event Scout will recommend events within this distance from your home location." />
                    </Label>
                    <Input
                      id="maxDistance"
                      type="number"
                      min="10"
                      max="500"
                      placeholder="50"
                      value={maxDistance}
                      onChange={(e) => setMaxDistance(e.target.value)}
                      className="input-default w-full"
                      data-testid="settings-max-distance"
                    />
                  </div>

                  <div className="p-4 bg-[#FFEDDF] rounded-xl border-2 border-[#1E3A5F]">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                          <Target className="w-5 h-5" />
                          View Community Events
                        </Label>
                        <p className="text-sm text-[#4A4A4A] mt-1">Access events shared by the AMSCompass community</p>
                      </div>
                      <Switch checked={viewCommunityEvents} onCheckedChange={setViewCommunityEvents} data-testid="settings-community-events-toggle" />
                    </div>
                  </div>

                  <div className="p-4 bg-[#FFEDDF] rounded-xl border-2 border-[#1E3A5F]">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1">
                        <Label className="font-medium text-[#1E3A5F] flex items-center gap-2">
                          <Layers className="w-5 h-5" />
                          Allow Inventory Overrides
                        </Label>
                        <p className="text-sm text-[#4A4A4A] mt-1">Allow allocations even when inventory is insufficient</p>
                      </div>
                      <Switch checked={allowNegativeInventory} onCheckedChange={setAllowNegativeInventory} data-testid="settings-allow-negative-inventory-toggle" />
                    </div>
                  </div>
                </div>

                {/* Artisan Profile */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <Sparkles className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Artisan Profile & Event Matching</h2>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="craftDescription" className="font-medium text-[#1E3A5F]">What do you make?</Label>
                    <Textarea
                      id="craftDescription"
                      placeholder="Describe your craft..."
                      value={craftDescription}
                      onChange={(e) => setCraftDescription(e.target.value)}
                      className="input-default w-full min-h-[80px]"
                      data-testid="settings-craft-description"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="showPreferences" className="font-medium text-[#1E3A5F]">Show Preferences</Label>
                    <Textarea
                      id="showPreferences"
                      placeholder="What types of shows do you prefer?"
                      value={showPreferences}
                      onChange={(e) => setShowPreferences(e.target.value)}
                      className="input-default w-full min-h-[80px]"
                      data-testid="settings-show-preferences"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label className="font-medium text-[#1E3A5F]">Preferred Venue Types</Label>
                      <div className="flex flex-wrap gap-2">
                        {VENUE_TYPES.map((type) => (
                          <div
                            key={type}
                            onClick={() => toggleVenueType(type)}
                            className={`px-3 py-1.5 rounded-lg border-2 cursor-pointer capitalize text-sm ${
                              preferredVenueTypes.includes(type) ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white border-gray-200 hover:border-[#1E3A5F]'
                            }`}
                          >
                            {type}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="font-medium text-[#1E3A5F]">Preferred Event Sizes</Label>
                      <div className="flex flex-wrap gap-2">
                        {EVENT_SIZES.map((size) => (
                          <div
                            key={size}
                            onClick={() => toggleEventSize(size)}
                            className={`px-3 py-1.5 rounded-lg border-2 cursor-pointer capitalize text-sm ${
                              preferredEventSizes.includes(size) ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white border-gray-200 hover:border-[#1E3A5F]'
                            }`}
                          >
                            {size}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="font-medium text-[#1E3A5F]">Preferred Regions</Label>
                    <div className="flex flex-wrap gap-2">
                      {US_REGIONS.map((region) => (
                        <div
                          key={region.value}
                          onClick={() => toggleRegion(region.value)}
                          className={`px-3 py-1.5 rounded-lg border-2 cursor-pointer text-sm ${
                            preferredRegions.includes(region.value) ? 'bg-[#1E3A5F] text-white border-[#1E3A5F]' : 'bg-white border-gray-200 hover:border-[#1E3A5F]'
                          }`}
                        >
                          {region.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Keywords */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <Label className="font-medium text-[#1E3A5F]">Keywords to Match</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., fine art, juried"
                          value={newMatchKeyword}
                          onChange={(e) => setNewMatchKeyword(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addMatchKeyword())}
                          className="input-default flex-1"
                        />
                        <Button type="button" onClick={addMatchKeyword} size="sm" className="btn-primary"><Plus className="w-4 h-4" /></Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {matchKeywords.map((k) => (
                          <span key={k} className="inline-flex items-center gap-1 px-2 py-1 bg-[#1E3A5F]/10 text-[#1E3A5F] rounded-full text-xs">
                            {k}<X className="w-3 h-3 cursor-pointer" onClick={() => removeMatchKeyword(k)} />
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Label className="font-medium text-[#1E3A5F]">Keywords to Avoid</Label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="e.g., flea market"
                          value={newAvoidKeyword}
                          onChange={(e) => setNewAvoidKeyword(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAvoidKeyword())}
                          className="input-default flex-1"
                        />
                        <Button type="button" onClick={addAvoidKeyword} size="sm" variant="outline" className="border-red-300 text-red-500"><Plus className="w-4 h-4" /></Button>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {avoidKeywords.map((k) => (
                          <span key={k} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-600 rounded-full text-xs">
                            {k}<X className="w-3 h-3 cursor-pointer" onClick={() => removeAvoidKeyword(k)} />
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button type="submit" className="btn-primary" disabled={loading} data-testid="settings-save-preferences-btn">
                    {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : 'Save Preferences'}
                  </Button>
                </div>
              </form>
            </TabsContent>

            {/* INTEGRATIONS TAB */}
            <TabsContent value="integrations">
              <div className="space-y-8">
                {/* Media Library Integrations */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <ImageIcon className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Media Library Integrations</h2>
                  </div>
                  <p className="text-sm text-[#4A4A4A]">Connect cloud storage to import images directly into your Media Library.</p>

                  {cloudLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" />
                    </div>
                  ) : (
                    <>
                      {/* Google Drive Integration */}
                      <div className={`p-6 bg-white border-2 rounded-xl transition-colors ${
                        cloudStatus?.google_drive ? 'border-green-500' : 'border-gray-200 hover:border-[#1E3A5F]'
                      }`}>
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#4285F4]/10 flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                              <path d="M4.5 14.5L8 9L15.5 9L12 14.5H4.5Z" fill="#4285F4"/>
                              <path d="M8 9L12 2L19.5 2L15.5 9H8Z" fill="#0F9D58"/>
                              <path d="M15.5 9L19.5 2L23 8.5L19 15.5L15.5 9Z" fill="#FFCD40"/>
                              <path d="M12 14.5L15.5 9L19 15.5L15.5 22H8.5L12 14.5Z" fill="#4285F4"/>
                              <path d="M4.5 14.5L8.5 22H15.5L12 14.5H4.5Z" fill="#F44336"/>
                              <path d="M1 8.5L4.5 14.5L8 9L4.5 2L1 8.5Z" fill="#0F9D58"/>
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-[#1E3A5F]">Google Drive</h3>
                              {cloudStatus?.google_drive && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                  <CheckCircle2 className="w-3 h-3" />
                                  Connected
                                </span>
                              )}
                            </div>
                            
                            {cloudStatus?.google_drive ? (
                              <>
                                <p className="text-sm text-[#4A4A4A] mb-3">
                                  Connected as <strong>{cloudStatus.google_drive_email}</strong>
                                </p>
                                <div className="p-3 bg-green-50 rounded-lg mb-4">
                                  <p className="text-xs text-green-700">
                                    <CheckCircle2 className="w-3 h-3 inline mr-1" />
                                    You can now import images from your Google Drive in the Media Library.
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Link to="/media-library">
                                    <Button 
                                      variant="outline" 
                                      className="border-2 border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F]/5"
                                    >
                                      <ImageIcon className="w-4 h-4 mr-2" />
                                      Go to Media Library
                                    </Button>
                                  </Link>
                                  <Button 
                                    variant="outline" 
                                    className="border-2 border-red-300 text-red-600 hover:bg-red-50"
                                    onClick={handleDisconnectGoogleDrive}
                                    disabled={disconnectingGoogle}
                                    data-testid="disconnect-google-drive-btn"
                                  >
                                    {disconnectingGoogle ? (
                                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Disconnecting...</>
                                    ) : (
                                      <><Unlink className="w-4 h-4 mr-2" />Disconnect</>
                                    )}
                                  </Button>
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="text-sm text-[#4A4A4A] mb-4">Import images directly from your Google Drive folders into your Media Library.</p>
                                <div className="p-3 bg-blue-50 rounded-lg mb-4">
                                  <p className="text-xs text-blue-700">
                                    <strong>Features:</strong> Browse folders, select multiple images, seamless import
                                  </p>
                                </div>
                                <Button 
                                  className="bg-[#4285F4] hover:bg-[#3367D6] text-white"
                                  onClick={handleConnectGoogleDrive}
                                  disabled={connectingGoogle}
                                  data-testid="connect-google-drive-btn"
                                >
                                  {connectingGoogle ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Connecting...</>
                                  ) : (
                                    <>
                                      <Link2 className="w-4 h-4 mr-2" />
                                      Connect Google Drive
                                    </>
                                  )}
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Dropbox Integration */}
                      <div className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-[#1E3A5F] transition-colors">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-[#0061FF]/10 flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#0061FF">
                              <path d="M6 2L12 6L6 10L0 6L6 2Z"/>
                              <path d="M18 2L24 6L18 10L12 6L18 2Z"/>
                              <path d="M0 14L6 10L12 14L6 18L0 14Z"/>
                              <path d="M18 10L24 14L18 18L12 14L18 10Z"/>
                              <path d="M6 19L12 15L18 19L12 23L6 19Z"/>
                            </svg>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-bold text-[#1E3A5F]">Dropbox</h3>
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                <CheckCircle2 className="w-3 h-3" />
                                Ready
                              </span>
                            </div>
                            <p className="text-sm text-[#4A4A4A] mb-4">Import images directly from your Dropbox folders into your Media Library.</p>
                            <div className="p-3 bg-blue-50 rounded-lg mb-4">
                              <p className="text-xs text-blue-700">
                                <strong>Features:</strong> Browse folders, select multiple images, seamless import
                              </p>
                            </div>
                            <p className="text-sm text-[#4A4A4A] mb-2">
                              <CheckCircle2 className="w-4 h-4 inline mr-1 text-green-500" />
                              Dropbox is ready to use! Go to Media Library and click "Import from Cloud" to select files.
                            </p>
                            <Link to="/media">
                              <Button 
                                className="bg-[#0061FF] hover:bg-[#004FC4] text-white"
                                data-testid="go-to-media-dropbox-btn"
                              >
                                <ImageIcon className="w-4 h-4 mr-2" />
                                Go to Media Library
                              </Button>
                            </Link>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Sales Channel Integrations - Coming Soon */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                    <ShoppingBag className="w-5 h-5 text-[#1E3A5F]" />
                    <h2 className="text-lg font-bold text-[#1E3A5F]">Sales Channel Integrations</h2>
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-600 text-xs rounded-full">Coming Soon</span>
                  </div>

                  {/* Etsy - Disabled */}
                  <div className="p-6 bg-gray-50 border-2 border-gray-200 rounded-xl opacity-60">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#F56400]/10 flex items-center justify-center flex-shrink-0">
                        <ShoppingBag className="w-6 h-6 text-[#F56400]" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#1E3A5F]">Etsy</h3>
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Coming Soon</span>
                        </div>
                        <p className="text-sm text-[#4A4A4A] mb-4">Import orders and track fees from your Etsy shop.</p>
                        <Button variant="outline" disabled className="opacity-50">
                          <ShoppingBag className="w-4 h-4 mr-2" />Connect Etsy Shop
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Shopify - Coming Soon */}
                  <div className="p-6 bg-gray-50 border-2 border-gray-200 rounded-xl opacity-60">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-[#96BF48]/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#96BF48">
                          <path d="M15.337 4.142c-.08-.087-.165-.13-.257-.13-.092 0-.18.043-.267.13l-1.16 1.16-1.16-1.16c-.087-.087-.175-.13-.267-.13-.092 0-.177.043-.257.13l-1.16 1.16-1.16-1.16c-.08-.087-.165-.13-.257-.13-.092 0-.18.043-.267.13l-1.16 1.16-1.16-1.16c-.087-.087-.175-.13-.267-.13-.092 0-.177.043-.257.13L4.9 5.303v13.394c0 .166.087.336.26.51l2.177 2.177c.174.174.344.26.51.26h9.306c.166 0 .336-.086.51-.26l2.177-2.177c.174-.174.26-.344.26-.51V5.303l-1.16-1.16-1.16 1.16-1.16-1.16z"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#1E3A5F]">Shopify</h3>
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Coming Soon</span>
                        </div>
                        <p className="text-sm text-[#4A4A4A] mb-4">Sync products and orders with your Shopify store.</p>
                        <Button variant="outline" disabled className="opacity-50">
                          Connect Shopify
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Square - Coming Soon */}
                  <div className="p-6 bg-gray-50 border-2 border-gray-200 rounded-xl opacity-60">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-black/10 flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6" viewBox="0 0 24 24" fill="#000">
                          <rect x="3" y="3" width="18" height="18" rx="2"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold text-[#1E3A5F]">Square</h3>
                          <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">Coming Soon</span>
                        </div>
                        <p className="text-sm text-[#4A4A4A] mb-4">Import in-person sales from Square POS.</p>
                        <Button variant="outline" disabled className="opacity-50">
                          Connect Square
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* BILLING TAB */}
            <TabsContent value="billing">
              <div className="space-y-8">
                {billingLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
                  </div>
                ) : (
                  <>
                    {/* Subscription Status */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                        <CreditCard className="w-5 h-5 text-[#1E3A5F]" />
                        <h2 className="text-lg font-bold text-[#1E3A5F]">Subscription Status</h2>
                      </div>

                      <div className="p-6 bg-gradient-to-r from-[#1E3A5F] to-[#2d4a6f] rounded-xl text-white">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-xl font-bold">AMSCompass</h3>
                              {getSubscriptionBadge()}
                            </div>
                            <p className="text-white/70 text-sm">
                              {subscription?.status === 'trial' && subscription?.trial_days_remaining !== null
                                ? `${subscription.trial_days_remaining} days remaining in your trial`
                                : subscription?.status === 'lifetime'
                                ? 'You have lifetime access to all features'
                                : subscription?.status === 'active'
                                ? 'Your subscription is active'
                                : subscription?.status === 'expired'
                                ? 'Your trial has expired'
                                : 'Manage your subscription below'}
                            </p>
                          </div>
                          {subscription?.plan && subscription.plan !== 'lifetime' && (
                            <div className="text-right">
                              <p className="text-white/70 text-xs">Current Plan</p>
                              <p className="text-lg font-bold capitalize">{subscription.plan}</p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Trial Info */}
                      {subscription?.status === 'trial' && (
                        <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <Clock className="w-5 h-5 text-blue-600 mt-0.5" />
                            <div>
                              <h4 className="font-medium text-blue-800">Free Trial</h4>
                              <p className="text-sm text-blue-700 mt-1">
                                {subscription.trial_days_remaining > 0 
                                  ? `You have ${subscription.trial_days_remaining} days left to explore all features.`
                                  : 'Your trial ends today!'}
                              </p>
                              {subscription.trial_end && (
                                <p className="text-xs text-blue-600 mt-2">
                                  <Calendar className="w-3 h-3 inline mr-1" />
                                  Trial ends: {new Date(subscription.trial_end).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Expired Warning */}
                      {subscription?.status === 'expired' && (
                        <div className="p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                            <div>
                              <h4 className="font-medium text-yellow-800">Trial Expired</h4>
                              <p className="text-sm text-yellow-700 mt-1">
                                Your free trial has ended. Subscribe now to continue using AMSCompass.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Manage Subscription (Stripe Customer Portal) — shown for active subscribers */}
                      {subscription?.status === 'active' && subscription?.plan && subscription.plan !== 'lifetime' && subscription.plan !== 'admin' && (
                        <div className="p-4 bg-white border-2 border-gray-200 rounded-xl">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div>
                              <h4 className="font-medium text-[#1E3A5F]">Manage Your Subscription</h4>
                              <p className="text-sm text-[#4A4A4A] mt-1">
                                Update your card, change plans, download invoices, or cancel anytime via Stripe&rsquo;s secure Customer Portal.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              className="border-[#1E3A5F] text-[#1E3A5F]"
                              data-testid="manage-subscription-btn"
                              onClick={async () => {
                                try {
                                  const res = await subscriptionAPI.openPortal(window.location.href);
                                  if (res.data?.portal_url) {
                                    window.location.href = res.data.portal_url;
                                  } else {
                                    toast.error('Could not open billing portal');
                                  }
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || 'Could not open billing portal');
                                }
                              }}
                            >
                              Manage Subscription
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Payment Processing Overlay */}
                    {paymentProcessing && (
                      <div className="p-6 bg-blue-50 border-2 border-blue-300 rounded-xl animate-pulse" data-testid="payment-processing">
                        <div className="flex items-center gap-4">
                          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                          <div>
                            <h4 className="font-bold text-blue-800 text-lg">Verifying Payment...</h4>
                            <p className="text-sm text-blue-700 mt-1">Please wait while we confirm your payment with Stripe.</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Payment Success Banner */}
                    {paymentSuccess && (
                      <div className="p-6 bg-green-50 border-2 border-green-300 rounded-xl" data-testid="payment-success">
                        <div className="flex items-center gap-4">
                          <CheckCircle2 className="w-8 h-8 text-green-600" />
                          <div>
                            <h4 className="font-bold text-green-800 text-lg">Payment Successful!</h4>
                            <p className="text-sm text-green-700 mt-1">Your subscription is now active. Thank you for subscribing!</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Pricing Plans */}
                    {subscription?.status !== 'lifetime' && subscription?.status !== 'active' && (
                      <div className="space-y-6">
                        <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                          <DollarSign className="w-5 h-5 text-[#1E3A5F]" />
                          <h2 className="text-lg font-bold text-[#1E3A5F]">Choose a Plan</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          {/* Monthly Plan */}
                          <div className="p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-[#1E3A5F] transition-colors">
                            <h3 className="font-bold text-[#1E3A5F] mb-2">Monthly</h3>
                            <div className="flex items-end gap-1 mb-4">
                              <span className="text-3xl font-bold text-[#1E3A5F]">${(pricing?.monthly_price || 9.99).toFixed(2)}</span>
                              <span className="text-gray-500 mb-1">USD/month</span>
                            </div>
                            <ul className="text-sm text-[#4A4A4A] space-y-2 mb-6">
                              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" />All features included</li>
                              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" />Cancel anytime</li>
                              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" />Priority support</li>
                            </ul>
                            <Button 
                              className="w-full btn-primary" 
                              data-testid="billing-monthly-btn"
                              onClick={async () => {
                                try {
                                  const res = await subscriptionAPI.createCheckout(window.location.origin, 'monthly');
                                  if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                                } catch (e) { toast.error('Failed to start checkout'); }
                              }}
                            >
                              Subscribe Monthly
                            </Button>
                          </div>

                          {/* Annual Plan */}
                          <div className="p-6 bg-[#1E3A5F]/5 border-2 border-[#1E3A5F] rounded-xl relative">
                            <div className="absolute -top-3 right-4 px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">
                              Save {pricing?.annual_discount_percent || 20}%
                            </div>
                            <h3 className="font-bold text-[#1E3A5F] mb-2">Annual</h3>
                            <div className="flex items-end gap-1 mb-4">
                              <span className="text-3xl font-bold text-[#1E3A5F]">${(pricing?.annual_price || 95.90).toFixed(2)}</span>
                              <span className="text-gray-500 mb-1">USD/year</span>
                            </div>
                            <ul className="text-sm text-[#4A4A4A] space-y-2 mb-6">
                              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" />All features included</li>
                              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" />Best value</li>
                              <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-green-500" />Priority support</li>
                            </ul>
                            <Button 
                              className="w-full bg-[#1E3A5F] hover:bg-[#2d4a6f] text-white" 
                              data-testid="billing-annual-btn"
                              onClick={async () => {
                                try {
                                  const res = await subscriptionAPI.createCheckout(window.location.origin, 'annual');
                                  if (res.data.checkout_url) window.location.href = res.data.checkout_url;
                                } catch (e) { toast.error('Failed to start checkout'); }
                              }}
                            >
                              Subscribe Annual
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Billing History Placeholder */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                        <Receipt className="w-5 h-5 text-[#1E3A5F]" />
                        <h2 className="text-lg font-bold text-[#1E3A5F]">Billing History</h2>
                      </div>

                      <div className="p-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl text-center">
                        <Receipt className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No billing history yet</p>
                        <p className="text-sm text-gray-400 mt-1">Your invoices and receipts will appear here</p>
                      </div>
                    </div>

                    {/* Payment Method Placeholder */}
                    <div className="space-y-6">
                      <div className="flex items-center gap-2 pb-2 border-b-2 border-[#E5E5E5]">
                        <CreditCard className="w-5 h-5 text-[#1E3A5F]" />
                        <h2 className="text-lg font-bold text-[#1E3A5F]">Payment Method</h2>
                      </div>

                      <div className="p-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl text-center">
                        <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-500">No payment method on file</p>
                        <p className="text-sm text-gray-400 mt-1">Add a payment method when you subscribe</p>
                        <Link to="/pricing">
                          <Button variant="outline" className="mt-4" data-testid="billing-add-payment-btn">
                            <Plus className="w-4 h-4 mr-2" />Add Payment Method
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Settings;
